import {createSelector} from 'reselect'
import isEqual from 'lodash/isEqual.js'
import isEmpty from 'lodash/isEmpty.js'
import omitBy from 'lodash/omitBy.js'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
} from '../../store.js'
import {
  DEFAULT_SORT,
  DEFAULT_PER_PAGE,
  DEFAULT_EXPAND_MODE,
  TRANSFER_SORT_OPTIONS,
  TRANSFER_URI_COMPONENT,
  TRANSFER_DATE_FILTERS,
  TRANSFER_API_DATE_FILTERS,
} from '../../common/constants/Transfers.js'
import api from '../../common/api.js'
import handleListSelection from '../../common/handleListSelection.js'
import {TRANSFER_LIST_PAGE} from '../../common/constants/Pages.js'
import {showGlobalError} from '../GlobalErrorMessage.js'
import {setTransfers} from '../../data/transfers.js'
import {navigate} from '../../common/location.js'
import {currentPageSelector} from '../../redux/selectors/ui/index.js'
import {locationSelector} from '../../redux/selectors/ui/location.js'
import {
  ensureArrayOfNumbers,
  ensureArrayOrUndefined,
  ensureBoolean,
} from '../../common/ensure.js'
import {isEmptyValue} from '../../common/utils.js'
import {
  EXPAND_MODE_EXPANDED,
  EXPAND_MODE_COLLAPSED,
} from '../../common/components/List/ExpandAllButton.js'
import {debouncedSaveUserSettings, userSettingSelector} from '../../data/me.js'
import {fromZonedTime, toZonedTime} from '../../common/zonedTime.js'
import {buildPath} from '../../common/querystring.js'

export const TRANSFER_LIST_FORM = 'transfer_list'
const TRANSFER__LIST__LIMIT = 'transfer.list.limit'
const TRANSFER__LIST__SORT = 'transfer.list.sort'
const TRANSFER__LIST__EXPAND_MODE = 'transfer.list.expand_mode'

export function transferListFormSelector(state) {
  return formsSelector(state)[TRANSFER_LIST_FORM]
}

export const transferIDListSelector = createSelector(
  transferListFormSelector,
  ({transferIDList} = {}) => transferIDList || [],
)

export function transferListQuerySelector(state) {
  transferListQuerySelector.cache = transferListQuerySelector.cache || {}
  const cache = transferListQuerySelector.cache

  const location = locationSelector(state)

  if (location === cache.location) {
    return cache.result
  }

  cache.location = location

  const result = {
    search: location.query.search || '',
    sort: location.query.sort || DEFAULT_SORT,
    limit: Number(location.query.limit) || DEFAULT_PER_PAGE,
    offset: Number(location.query.offset) || 0,
    sending_warehouse_id: ensureArrayOfNumbers(
      location.query.sending_warehouse_id,
    ),
    receiving_warehouse_id: ensureArrayOfNumbers(
      location.query.receiving_warehouse_id,
    ),
    ...summarizeTransferDateRangeParams(location.query),
  }

  if (isEqual(cache.result, result)) {
    return cache.result
  }

  cache.result = result

  return result
}

export const isLoadingSelector = createSelector(
  transferListFormSelector,
  ({isLoading} = {}) => isLoading || false,
)

export const countSelector = createSelector(
  transferListFormSelector,
  ({count} = {}) => count || 0,
)

export function expandModeSelector(state) {
  return userSettingSelector(state, {
    setting: TRANSFER__LIST__EXPAND_MODE,
    defaultValue: DEFAULT_EXPAND_MODE,
  })
}

export const expandedTransferIDsSelector = createSelector(
  transferListFormSelector,
  ({expandedTransferIDs} = {}) => expandedTransferIDs || [],
)

export const selectedTransferIDsSelector = createSelector(
  transferListFormSelector,
  ({selectedTransferIDs} = {}) => selectedTransferIDs || [],
)

export const allSelectedSelector = createSelector(
  transferIDListSelector,
  selectedTransferIDsSelector,
  (transferIDList, selected) =>
    transferIDList.length > 0 && transferIDList.length === selected.length,
)

export const indeterminateSelectedSelector = createSelector(
  allSelectedSelector,
  selectedTransferIDsSelector,
  (allSelected, selectedTransferIDs) =>
    !allSelected && selectedTransferIDs.length > 0,
)

export function navigateToTransferListPage(query) {
  const defaultQuery = defaultHashParamsSelector(getState())

  return navigate([TRANSFER_URI_COMPONENT], {...defaultQuery, ...query})
}

export function defaultHashParamsSelector(state) {
  const limit = userSettingSelector(state, {
    setting: TRANSFER__LIST__LIMIT,
    defaultValue: DEFAULT_PER_PAGE,
  })
  const sort = userSettingSelector(state, {
    setting: TRANSFER__LIST__SORT,
    defaultValue: DEFAULT_SORT,
  })

  return {
    limit: limit !== DEFAULT_PER_PAGE ? limit : undefined,
    sort: sort !== DEFAULT_SORT ? sort : undefined,
  }
}

export function queryParamsSelector(state) {
  const {
    search,
    sort,
    sending_warehouse_id,
    receiving_warehouse_id,
    limit,
    offset,
    ...query
  } = transferListQuerySelector(state)

  return {
    search: search.trim() || undefined,
    sending_warehouse_id: sending_warehouse_id.length
      ? sending_warehouse_id
      : undefined,
    receiving_warehouse_id: receiving_warehouse_id.length
      ? receiving_warehouse_id
      : undefined,
    ...getTransferDateRangeParams(
      query.start_date,
      query.end_date,
      query.date_type,
      query.interval_type,
      query.interval_amount,
      query.interval_trunc,
      query.timezone,
    ),
    sort,
    limit,
    offset,
  }
}

export function cleanTransferQuery(query) {
  return Object.entries(query)
    .filter(([key, value]) => {
      if (
        ![
          'limit',
          'offset',
          'sending_warehouse_id',
          'receiving_warehouse_id',
          'search',
          'sort',
          ...TRANSFER_API_DATE_FILTERS,
        ].includes(key)
      ) {
        return false
      }

      if (Array.isArray(value) ? value.length === 0 : isEmptyValue(value)) {
        return false
      }

      return true
    })
    .reduce((prev, [key, value]) => {
      prev[key] = value
      return prev
    }, {})
}

export const createIsSelectedSelector = (transferID) =>
  createSelector(selectedTransferIDsSelector, (selectedTransferIDs) => {
    return selectedTransferIDs.includes(transferID)
  })

export const createIsExpandedSelector = (transferID) =>
  createSelector(
    expandedTransferIDsSelector,
    expandModeSelector,
    (expandedTransferIDs, expandMode) => {
      if (expandMode === EXPAND_MODE_COLLAPSED) {
        return expandedTransferIDs.includes(transferID)
      }
      if (expandMode === EXPAND_MODE_EXPANDED) {
        return !expandedTransferIDs.includes(transferID)
      }
      return false
    },
  )

export const isFilteredSelector = createSelector(
  queryParamsSelector,
  (queryParams) =>
    !isEmpty(
      omitBy(queryParams, (value, key) =>
        ['sort', 'limit', 'offset'].includes(key),
      ),
    ),
)

export function getTransferDateRangeParams(
  startDate,
  endDate,
  dateFilterType,
  intervalType,
  intervalAmount,
  intervalTrunc,
  timezone,
) {
  const params = {}

  if (startDate) {
    startDate = new Date(startDate)

    const startDateString = startDate.toISOString()

    if (TRANSFER_DATE_FILTERS.includes(dateFilterType)) {
      params[`${dateFilterType}_after`] = startDateString
    }
  }

  if (endDate) {
    endDate = new Date(endDate)

    const endDateString = endDate.toISOString()

    if (TRANSFER_DATE_FILTERS.includes(dateFilterType)) {
      params[`${dateFilterType}_before`] = endDateString
    }
  }

  if (intervalType) {
    intervalTrunc = ensureBoolean(intervalTrunc)

    if (TRANSFER_DATE_FILTERS.includes(dateFilterType)) {
      params[`${dateFilterType}_interval_type`] = intervalType
      params[`${dateFilterType}_interval_amount`] = intervalAmount
      params[`${dateFilterType}_interval_trunc`] = intervalTrunc
    }
  }

  if (Object.keys(params).length) {
    params.timezone = timezone
  }

  return params
}

export function summarizeTransferDateRangeParams(query) {
  let start_date
  let end_date
  let date_type
  let interval_type
  let interval_amount
  let interval_trunc
  let timezone

  if (query.timezone) {
    timezone = query.timezone
  }

  for (const dateType of TRANSFER_DATE_FILTERS) {
    if (query[`${dateType}_after`]) {
      start_date = new Date(query[`${dateType}_after`])
      date_type = dateType
    }
  }

  if (start_date && timezone) {
    start_date = toZonedTime(start_date, timezone)
  }

  for (const dateType of TRANSFER_DATE_FILTERS) {
    if (query[`${dateType}_before`]) {
      end_date = new Date(query[`${dateType}_before`])
      date_type = dateType
    }
  }

  if (end_date && timezone) {
    end_date = toZonedTime(end_date, timezone)
  }

  for (const dateType of TRANSFER_DATE_FILTERS) {
    if (query[`${dateType}_interval_type`]) {
      interval_type = query[`${dateType}_interval_type`]
      interval_amount = query[`${dateType}_interval_amount`]
      interval_trunc = ensureBoolean(query[`${dateType}_interval_trunc`])
      date_type = dateType
    }
  }

  return {
    start_date,
    end_date,
    date_type,
    interval_type,
    interval_amount,
    interval_trunc,
    timezone,
  }
}

export function updateTransferDateRangeFilters({
  start_date,
  end_date,
  date_type,
  interval_type,
  interval_amount,
  interval_trunc,
  timezone,
} = {}) {
  const updates = {
    ...TRANSFER_API_DATE_FILTERS.reduce((prev, property) => {
      prev[property] = undefined
      return prev
    }, {}),
    timezone: undefined,
    ...resetOffset(),
  }

  if (interval_type) {
    updates[`${date_type}_interval_type`] = interval_type
    updates[`${date_type}_interval_amount`] = Number(interval_amount) || 1
    updates[`${date_type}_interval_trunc`] = !!interval_trunc
    updates.timezone = timezone
  } else if (start_date || end_date) {
    if (start_date) {
      start_date = startOfDay(start_date)

      if (timezone) {
        start_date = fromZonedTime(start_date, timezone)

        updates.timezone = timezone
      }

      updates[`${date_type}_after`] = start_date.toISOString()
    }

    if (end_date) {
      end_date = endOfDay(end_date)

      if (timezone) {
        end_date = fromZonedTime(end_date, timezone)

        updates.timezone = timezone
      }

      updates[`${date_type}_before`] = end_date.toISOString()
    }
  }

  return updates
}

export function setTransferDateRangeFilters(dateRangeFilters) {
  return navigateTransferList(updateTransferDateRangeFilters(dateRangeFilters))
}

export function setTimezone(timezone) {
  let {
    start_date,
    end_date,
    date_type,
    interval_type,
    interval_amount,
    interval_trunc,
  } = transferListQuerySelector(getState())

  return navigateTransferList(
    updateTransferDateRangeFilters({
      start_date,
      end_date,
      date_type,
      interval_type,
      interval_amount,
      interval_trunc,
      timezone,
    }),
  )
}

export function updateTimezone(timezone) {
  return {
    timezone: timezone || undefined,
    ...resetOffset(),
  }
}

export function setupTransferListForm() {
  setForm(TRANSFER_LIST_FORM, {
    isLoading: false,
    count: 0,
    transferIDList: [],
    selectedTransferIDs: [],
    expandedTransferIDs: [],
  })
}

export function updateTransferListForm(updates, meta) {
  updateForm(TRANSFER_LIST_FORM, updates, meta)
}

export function removeTransferListForm() {
  removeForm(TRANSFER_LIST_FORM)
}

export function navigateTransferList(updates = {}) {
  let {pathComponents, query} = locationSelector(getState())

  if (pathComponents[0] !== TRANSFER_URI_COMPONENT) {
    return
  } else {
    query = {...query, ...updates}
  }

  return navigate(pathComponents, query)
}

export function resetOffset() {
  return {
    offset: undefined,
  }
}

export function updateLimit(limit) {
  limit = Number(limit)

  if (![10, 50, 100].includes(limit)) {
    limit = DEFAULT_PER_PAGE
  }

  if (limit === DEFAULT_PER_PAGE) {
    limit = undefined
  }

  return {
    limit,
    ...resetOffset(),
  }
}

export function setLimit(limit) {
  const updates = updateLimit(limit)

  debouncedSaveUserSettings({[TRANSFER__LIST__LIMIT]: updates.limit})

  return navigateTransferList(updates)
}

export function updateSort(sort) {
  if (!TRANSFER_SORT_OPTIONS.find(({value}) => sort === value)) {
    sort = DEFAULT_SORT
  }

  if (sort === DEFAULT_SORT) {
    sort = undefined
  }

  return {
    sort,
    ...resetOffset(),
  }
}

export function setSort(sort) {
  const updates = updateSort(sort)

  debouncedSaveUserSettings({[TRANSFER__LIST__SORT]: updates.sort})

  return navigateTransferList(updates)
}

export function updateSearchText(search) {
  search = (search || '').trim() || undefined

  return {
    search,
    ...resetOffset(),
  }
}

export function setSearchText(search) {
  return navigateTransferList(updateSearchText(search))
}

export function updateTransferSendingWarehouseIDs(warehouseIDs) {
  return {
    sending_warehouse_id: ensureArrayOrUndefined(warehouseIDs),
    ...resetOffset(),
  }
}

export function setTransferSendingWarehouseIDs(warehouseIDs) {
  return navigateTransferList(updateTransferSendingWarehouseIDs(warehouseIDs))
}

export function removeTransferSendingWarehouseID(warehouseID) {
  const {sending_warehouse_id} = transferListQuerySelector(getState())

  return setTransferSendingWarehouseIDs(
    sending_warehouse_id.filter((id) => id !== warehouseID),
  )
}

export function updateTransferReceivingWarehouseIDs(warehouseIDs) {
  return {
    receiving_warehouse_id: ensureArrayOrUndefined(warehouseIDs),
    ...resetOffset(),
  }
}

export function setTransferReceivingWarehouseIDs(warehouseIDs) {
  return navigateTransferList(updateTransferReceivingWarehouseIDs(warehouseIDs))
}

export function removeTransferReceivingWarehouseID(warehouseID) {
  const {receiving_warehouse_id} = transferListQuerySelector(getState())

  return setTransferReceivingWarehouseIDs(
    receiving_warehouse_id.filter((id) => id !== warehouseID),
  )
}

export function updateOffset(offset) {
  offset = Number(offset) || 0

  if (!offset) {
    offset = undefined
  }

  return {
    offset,
  }
}

export function setOffset(offset) {
  return navigateTransferList(updateOffset(offset))
}

export function setExpandedTransferIDs(expandedTransferIDs) {
  updateTransferListForm({
    expandedTransferIDs,
  })
}

export function setExpandMode(expandMode) {
  debouncedSaveUserSettings({[TRANSFER__LIST__EXPAND_MODE]: expandMode})
}

export function setSelectedTransferIDs(selectedTransferIDs) {
  updateTransferListForm({
    selectedTransferIDs,
  })
}

export async function refreshTransferList() {
  try {
    const currentPage = currentPageSelector(getState())
    if (currentPage !== TRANSFER_LIST_PAGE) {
      return
    }

    updateTransferListForm({isLoading: true})

    const params = queryParamsSelector(getState())

    const {json} = await api.get(buildPath([TRANSFER_URI_COMPONENT]), params)

    setTransfers(json[TRANSFER_URI_COMPONENT])

    updateQueryResults(json)
  } catch (err) {
    showGlobalError(
      {
        summary: 'Error querying Transfers.',
        details: err.message || err.error_message,
      },
      err,
    )
  }

  updateTransferListForm({isLoading: false})
}

export function updateQueryResults({count, [TRANSFER_URI_COMPONENT]: list}) {
  const {selectedTransferIDs, expandedTransferIDs} =
    transferListFormSelector(getState())
  const transferIDList = list.map((transfer) => transfer.transfer_id)

  updateTransferListForm({
    transferIDList,
    count,
    selectedTransferIDs: selectedTransferIDs.filter((transferID) =>
      transferIDList.includes(transferID),
    ),
    expandedTransferIDs: expandedTransferIDs.filter((transferID) =>
      transferIDList.includes(transferID),
    ),
  })
}

export async function transferListSelectTransferID(
  transferID,
  isSelected,
  isShiftKey,
) {
  const selectedTransferIDs = selectedTransferIDsSelector(getState())
  const transferIDList = transferIDListSelector(getState())

  setSelectedTransferIDs(
    handleListSelection({
      value: transferID,
      isSelected,
      isShiftKey,
      selected: selectedTransferIDs,
      list: transferIDList,
    }),
  )
}

export async function deselectTransfers(transferIDs) {
  const selectedTransferIDs = selectedTransferIDsSelector(getState())

  setSelectedTransferIDs(
    selectedTransferIDs.filter((id) => !transferIDs.includes(id)),
  )
}

export async function transferListToggleExpanded(transferID) {
  const expandedTransferIDs = expandedTransferIDsSelector(getState())

  const transferIDs = expandedTransferIDs.filter((p) => p !== transferID)

  if (!expandedTransferIDs.includes(transferID)) {
    transferIDs.push(transferID)
  }

  setExpandedTransferIDs(transferIDs)
}
